import { useEffect } from "react";

/**
 * Scrolls the window to the top whenever `key` changes, and *holds* it there
 * for a short window afterwards.
 *
 * A single scrollTo isn't enough on step-style pages. The new step's cards
 * finish loading after the first paint, and the resulting layout shift — plus
 * the browser's scroll anchoring, which adjusts scrollTop to keep an in-view
 * element stable while content above it resizes — can drag the viewport back
 * down a moment later. Re-asserting across a few frames outlasts that.
 *
 * Any real scroll intent from the user cancels the hold immediately, so this
 * can never fight someone who has started reading.
 */
const useScrollToTop = (key, { holdMs = 500 } = {}) => {
  useEffect(() => {
    let frame;
    let cancelled = false;
    const startedAt = performance.now();

    const release = () => {
      cancelled = true;
    };

    // Passive listeners: we only observe intent, never block the gesture.
    window.addEventListener("wheel", release, { passive: true });
    window.addEventListener("touchmove", release, { passive: true });
    window.addEventListener("keydown", release);

    const hold = (now) => {
      if (cancelled) return;
      if (window.scrollY !== 0) window.scrollTo(0, 0);
      if (now - startedAt < holdMs) frame = requestAnimationFrame(hold);
    };

    window.scrollTo(0, 0);
    frame = requestAnimationFrame(hold);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      window.removeEventListener("wheel", release);
      window.removeEventListener("touchmove", release);
      window.removeEventListener("keydown", release);
    };
  }, [key, holdMs]);
};

export default useScrollToTop;
